import { type CredentialProvisionType } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { type AdminMessage } from "@sendbird/chat/message";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { parseChannelUrl } from "@src/appV2/redesign/Chat/constants";
import { useWorker } from "@src/appV2/Worker/api/useWorker";
import { useChartingCredentialProvisioningConfig } from "@src/appV2/WorkerCredentials/api/useCredentialProvisioningConfig";
import { Span, Text, type UseModalState } from "@src/lib/ui-react";

interface SendbirdWorkerCredentialProvisionedMessageProps {
  message: AdminMessage;
  channelUrl: string;
  manageCredentialsModalState?: UseModalState;
}

function parseWorkerCredentialProvisionedMessage(
  message: AdminMessage
): CredentialProvisionType | undefined {
  try {
    const parsedData = JSON.parse(message.data) as { requestType: CredentialProvisionType };
    const { requestType } = parsedData;
    return requestType;
  } catch (error) {
    logError("Failed to parse worker credential requested message", {
      error,
      metadata: { message },
    });
    return undefined;
  }
}

export function WorkerCredentialProvisionedMessage(
  props: SendbirdWorkerCredentialProvisionedMessageProps
) {
  const { message, channelUrl, manageCredentialsModalState } = props;

  const { workerId } = parseChannelUrl(channelUrl) ?? {};
  const { data: workerData } = useWorker(workerId);
  const workerName = workerData?.name ?? "Worker";

  const chartingCredentialIntegrationConfig = useChartingCredentialProvisioningConfig();
  if (!chartingCredentialIntegrationConfig.enabled) {
    return null;
  }

  const requestType = parseWorkerCredentialProvisionedMessage(message);
  if (!isDefined(requestType)) {
    logError(APP_V2_APP_EVENTS.WORKER_CREDENTIAL_PROVISIONED_MESSAGE_RENDER_FAILURE, {
      error: "Failed to render WorkerCredentialProvisionedMessage",
      metadata: {
        messageId: message.messageId,
      },
    });
    return null;
  }

  const software = "PointClickCare";

  const requestTypeToMessage: Record<CredentialProvisionType, string> = {
    // Create and update credentials are treated the same
    create_credentials: `A new ${software} account has been created for ${workerName}.`,
    update_credentials: `A new ${software} account has been created for ${workerName}.`,
    reset_password: `${workerName}'s ${software} account was reset.`,
  };

  const messageText = requestTypeToMessage[requestType];

  return (
    <div className="sendbird-admin-message">
      <Text
        variant="body2"
        textAlign="center"
        className="sendbird-label--caption-2 worker-credential-requested-message"
      >
        {messageText}{" "}
        <Span
          // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
          role="button"
          tabIndex={0}
          className="worker-credential-requested-message-link"
          onClick={() => {
            manageCredentialsModalState?.openModal();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              manageCredentialsModalState?.openModal();
            }
          }}
        >
          View details
        </Span>
      </Text>
    </div>
  );
}
